import React, { useState, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2, Receipt, Copy, Landmark, Star, ChevronLeft, ChevronRight, CalendarRange, Archive, RotateCcw } from "lucide-react";
import { Link } from "react-router-dom";
import api, { fmtEur } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { toast } from "sonner";
import { monthLabel, thisMonth, monthOptions, CATS, CAT_META, catOf, GENERALI_ID, dedNum, fornitoreIcon } from "./spese/helpers";
import SpeseTabellaF24 from "./spese/SpeseTabellaF24";
import SpeseTabellaFiscale from "./spese/SpeseTabellaFiscale";

export default function SpeseSheet() {
  const { can, selectedStructure, structures = [] } = useAuth();
  const qc = useQueryClient();
  const [month, setMonth] = useState(thisMonth());
  const [copying, setCopying] = useState(false);
  const [confirmCopy, setConfirmCopy] = useState(false);
  const [soloNonPagate, setSoloNonPagate] = useState(false);
  const [showArch, setShowArch] = useState(false);
  const realStructs = structures.filter((s) => s.id !== "all");
  const structOptions = [...realStructs, { id: GENERALI_ID, nome: "Generali (società)" }];
  const showStruct = selectedStructure === "all";

  const { data: spese = [], isLoading } = useQuery({
    queryKey: ["spese-sheet", selectedStructure],
    queryFn: async () => (await api.get("/spese", { params: { structure_id: selectedStructure } })).data,
  });
  const { data: fornitori = [] } = useQuery({
    queryKey: ["fornitori-opts"],
    queryFn: async () => (await api.get("/fornitori", { params: { structure_id: "all" } })).data,
  });
  const { data: archived = [] } = useQuery({
    queryKey: ["spese-archivio", selectedStructure],
    queryFn: async () => (await api.get("/spese", { params: { structure_id: selectedStructure, archived: "true" } })).data,
    enabled: showArch,
  });

  const monthRows = useMemo(() => spese.filter((r) => (r.data_documento || "").slice(0, 7) === month).sort((a, b) => (a.created_at || "").localeCompare(b.created_at || "")), [spese, month]);
  const structMap = Object.fromEntries(structOptions.map((s) => [s.id, s]));
  const structAbbr = (nome) => {
    const n = (nome || "").toLowerCase();
    if (n.includes("general")) return "GEN";
    if (n.includes("boschett")) return "BOS";
    if (n.includes("villa")) return "VIL";
    if (n.includes("depand") || n.includes("dépend") || n.includes("dependance")) return "DEP";
    return (nome || "—").slice(0, 3).toUpperCase();
  };
  const fornitoriSorted = useMemo(() => [...fornitori].sort((a, b) => (a.nome || "").localeCompare(b.nome || "", "it")), [fornitori]);
  const fornitoriSpese = useMemo(() => fornitoriSorted.filter((f) => (f.categoria || "") !== "utenze"), [fornitoriSorted]);
  const fornitoriUtenze = useMemo(() => fornitoriSorted.filter((f) => (f.categoria || "") === "utenze"), [fornitoriSorted]);
  const invalidate = () => qc.invalidateQueries({ queryKey: ["spese-sheet"] });

  const patch = async (row, changes) => {
    qc.setQueryData(["spese-sheet", selectedStructure], (old = []) => old.map((r) => (r.id === row.id ? { ...r, ...changes } : r)));
    try { await api.put(`/spese/${row.id}`, changes); }
    catch { toast.error("Errore aggiornamento"); invalidate(); }
  };
  const addRow = async (categoria) => {
    const sid = selectedStructure !== "all" ? selectedStructure : GENERALI_ID;
    if (!sid) { toast.error("Nessuna struttura disponibile"); return; }
    try {
      await api.post("/spese", { categoria, structure_id: sid, data_documento: `${month}-01`, totale: 0, stato: "non pagato", fornitore: "", descrizione: "" });
      invalidate();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore creazione"); }
  };
  const addF24Row = async () => {
    const sid = selectedStructure !== "all" ? selectedStructure : GENERALI_ID;
    if (!sid) { toast.error("Nessuna struttura disponibile"); return; }
    try {
      await api.post("/spese", { categoria: "varie", da_f24: true, iva_ritenuta: 0, imponibile: 0, structure_id: sid, data_documento: `${month}-01`, totale: 0, stato: "non pagato", fornitore: "", descrizione: "" });
      invalidate();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore creazione"); }
  };
  const addFiscaleRow = async () => {
    const sid = selectedStructure !== "all" ? selectedStructure : GENERALI_ID;
    if (!sid) { toast.error("Nessuna struttura disponibile"); return; }
    try {
      await api.post("/spese", { categoria: "varie", fiscale_speciale: true, deducibilita_perc: 100, capitalizza: false, aliquota_amm: 0, structure_id: sid, data_documento: `${month}-01`, totale: 0, stato: "non pagato", fornitore: "", descrizione: "" });
      invalidate();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore creazione"); }
  };
  const removeRow = async (id) => {
    try { await api.delete(`/spese/${id}`); invalidate(); toast.success("Spostata in archivio"); }
    catch { toast.error("Errore eliminazione"); }
  };
  const restoreRow = async (id) => {
    try {
      await api.post(`/spese/${id}/restore`); invalidate();
      qc.invalidateQueries({ queryKey: ["spese-archivio"] });
      toast.success("Voce ripristinata");
    } catch { toast.error("Errore ripristino"); }
  };
  const copiaMese = async () => {
    setCopying(true);
    try {
      const { data } = await api.post("/spese/copia-mese", { mese: month, structure_id: selectedStructure });
      invalidate();
      toast.success(data.created ? `Copiate ${data.created} voci da ${monthLabel(data.fonte)}` : `Nessuna voce da copiare in ${monthLabel(data.fonte)}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Errore nella copia"); }
    finally { setCopying(false); setConfirmCopy(false); }
  };
  const generaRicorrenti = async () => {
    try {
      const { data } = await api.post("/spese/genera-ricorrenti", { mese: month, structure_id: selectedStructure });
      invalidate();
      toast.success(data.created ? `Inserite ${data.created} voci ricorrenti in ${monthLabel(month)}` : "Tutte le voci ricorrenti sono già presenti");
    } catch (e) { toast.error(e.response?.data?.detail || "Errore generazione ricorrenti"); }
  };
  const addSingleRicorrente = async (tpl) => {
    try {
      await api.post("/spese", {
        categoria: tpl.categoria || "varie", structure_id: tpl.structure_id,
        fornitore: tpl.fornitore || "", descrizione: tpl.descrizione || "",
        data_documento: `${month}-01`, totale: Number(tpl.totale || 0), stato: "non pagato", ricorrente: true,
        iva_ritenuta: tpl.iva_ritenuta || 0, imponibile: tpl.imponibile || 0, da_f24: !!tpl.da_f24,
        fiscale_speciale: !!tpl.fiscale_speciale, capitalizza: !!tpl.capitalizza,
        aliquota_amm: tpl.aliquota_amm || 0, deducibilita_perc: tpl.deducibilita_perc ?? 100,
      });
      invalidate();
      toast.success("Voce ricorrente aggiunta");
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const shiftMonth = (delta) => {
    const [y, m] = month.split("-").map(Number);
    const d = new Date(y, m - 1 + delta, 1);
    setMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
  };

  if (!can("spese")) return <div className="gr-card p-10 text-center text-muted-foreground">Non hai i permessi per questa sezione.</div>;

  const visible = soloNonPagate ? monthRows.filter((r) => (r.stato || "") !== "pagato") : monthRows;
  const shownTotal = visible.reduce((s, r) => s + Number(r.totale || 0), 0);
  const f24Rows = visible.filter((r) => Number(r.iva_ritenuta || 0) > 0 || r.da_f24);
  const f24Total = f24Rows.reduce((s, r) => s + Number(r.iva_ritenuta || 0), 0);
  const f24RowsTotal = f24Rows.reduce((s, r) => s + Number(r.totale || 0), 0);
  const f24ImpTot = f24Rows.reduce((s, r) => s + Number(r.imponibile || 0), 0);
  const fiscRows = visible.filter((r) => r.fiscale_speciale || r.capitalizza || dedNum(r) < 100);
  const fiscCapTot = fiscRows.filter((r) => r.capitalizza).reduce((s, r) => s + Number(r.totale || 0), 0);
  const fiscIndeducTot = fiscRows.filter((r) => !r.capitalizza).reduce((s, r) => s + Number(r.totale || 0) * (1 - dedNum(r) / 100), 0);
  const fiscImportoTot = fiscRows.reduce((s, r) => s + Number(r.totale || 0), 0);
  const keyOf = (r) => `${r.structure_id}|${catOf(r)}|${r.fornitore || ""}|${r.descrizione || ""}`;
  const monthKeys = new Set(monthRows.map(keyOf));
  const templateMap = {};
  spese.forEach((r) => { if (r.ricorrente) { const k = keyOf(r); if (!templateMap[k]) templateMap[k] = r; } });
  const missingTemplates = Object.entries(templateMap).filter(([k]) => !monthKeys.has(k)).map(([, r]) => r);
  const missingRicorrenti = missingTemplates.length;
  const colSpanBase = showStruct ? 6 : 5;

  return (
    <div className="pb-20" data-testid="spese-sheet-page">
      <div className="mb-5 flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-head font-bold flex items-center gap-2"><Receipt size={22} className="text-primary" /> Spese</h1>
          <p className="text-sm text-muted-foreground">Inserisci tutte le spese del mese suddivise per categoria — clicca sulle celle per modificare</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" onClick={generaRicorrenti} data-testid="spese-genera-ricorrenti-btn">
            <Star size={15} className="mr-1" /> Genera ricorrenti
          </Button>
          <Button variant="outline" onClick={() => setConfirmCopy(true)} disabled={copying} data-testid="spese-copy-prev-btn">
            <Copy size={15} className="mr-1" /> {copying ? "Copio..." : "Copia dal mese scorso"}
          </Button>
          <Button variant="outline" onClick={() => setShowArch(true)} data-testid="spese-archivio-btn">
            <Archive size={15} className="mr-1" /> Archivio
          </Button>
          <button onClick={() => setSoloNonPagate((v) => !v)} data-testid="spese-filter-nonpagate"
            className={`h-11 px-3 rounded-lg border text-sm font-semibold transition-colors ${soloNonPagate ? "bg-rose-500 text-white border-rose-500" : "hover:bg-secondary"}`}>
            Solo non pagate
          </button>
          <Button variant="outline" size="icon" className="h-11 w-11" onClick={() => shiftMonth(-1)} data-testid="spese-month-prev" title="Mese precedente"><ChevronLeft size={18} /></Button>
          <div className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-primary text-white shadow-sm" data-testid="spese-month-badge">
            <CalendarRange size={16} />
            <span className="font-head font-bold capitalize">{monthLabel(month)}</span>
          </div>
          <Button variant="outline" size="icon" className="h-11 w-11" onClick={() => shiftMonth(1)} data-testid="spese-month-next" title="Mese successivo"><ChevronRight size={18} /></Button>
          <Select value={month} onValueChange={setMonth}>
            <SelectTrigger className="h-11 w-44 font-semibold" data-testid="spese-month-select"><SelectValue /></SelectTrigger>
            <SelectContent className="max-h-72">
              {monthOptions().map((k) => <SelectItem key={k} value={k} data-testid={`spese-month-${k}`}>{monthLabel(k)}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {isLoading ? <div className="gr-card p-10 text-center text-muted-foreground">Caricamento...</div> : (
      <div className="space-y-5">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h2 className="text-sm font-bold uppercase tracking-wide text-muted-foreground">Riepilogo totali · {monthLabel(month)}</h2>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="spese-riepilogo-categorie">
          {CATS.map((cat) => {
            const meta = CAT_META[cat];
            const tot = visible.filter((r) => catOf(r) === cat).reduce((s, r) => s + Number(r.totale || 0), 0);
            const Icon = meta.icon;
            return (
              <div key={cat} className="gr-card p-3 border-l-4 min-w-0" style={{ borderColor: meta.color }} data-testid={`spese-box-${cat}`}>
                <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-muted-foreground min-w-0">
                  <Icon size={13} style={{ color: meta.color }} className="shrink-0" /> <span className="truncate">{meta.label}</span>
                </div>
                <div className="text-base font-head font-bold tabnum mt-1 break-all" style={{ color: meta.color }}>{fmtEur(tot)}</div>
              </div>
            );
          })}
          <div className="gr-card p-3 border-l-4 border-indigo-500 min-w-0" data-testid="spese-box-f24">
            <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-muted-foreground min-w-0">
              <Landmark size={13} className="text-indigo-600 shrink-0" /> <span className="truncate">Spese IVA/Rit.</span>
            </div>
            <div className="text-base font-head font-bold tabnum mt-1 text-indigo-700 break-all">{fmtEur(f24Total)}</div>
            <div className="text-[10px] font-semibold text-muted-foreground mt-0.5">IVA/Rit. da versare · su spesa {fmtEur(f24RowsTotal)}</div>
          </div>
          <div className="gr-card p-3 border-l-4 border-primary bg-primary/5 min-w-0" data-testid="spese-box-totale">
            <div className="text-[11px] font-bold uppercase tracking-wide text-primary truncate">{soloNonPagate ? "Da pagare" : "Totale"}</div>
            <div className="text-base font-head font-bold tabnum mt-1 text-primary break-all">{fmtEur(shownTotal)}</div>
          </div>
        </div>
        {missingRicorrenti > 0 && (
          <div className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3" data-testid="spese-ricorrenti-banner">
            <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
              <span className="text-sm font-medium text-amber-800"><Star size={15} className="inline mr-1 -mt-0.5" /> <b>{missingRicorrenti}</b> voci ricorrenti non ancora inserite in {monthLabel(month)}. Scegli quali aggiungere:</span>
              <Button size="sm" onClick={generaRicorrenti} className="font-semibold" data-testid="spese-ricorrenti-genera">Aggiungi tutte</Button>
            </div>
            <div className="flex flex-col gap-1.5">
              {missingTemplates.map((tpl) => (
                <div key={tpl.id} className="flex items-center justify-between gap-2 bg-white/70 border border-amber-200 rounded-md px-2.5 py-1.5" data-testid={`spese-ricorrente-missing-${tpl.id}`}>
                  <div className="flex items-center gap-2 min-w-0 text-xs">
                    <Star size={12} className="fill-amber-400 text-amber-500 shrink-0" />
                    {showStruct && <span className="text-[10px] font-semibold uppercase text-muted-foreground shrink-0">{structMap[tpl.structure_id]?.nome || "—"}</span>}
                    <span className="font-semibold px-1.5 py-0.5 rounded bg-secondary text-muted-foreground text-[10px] uppercase shrink-0">{tpl.da_f24 ? "F24" : tpl.fiscale_speciale ? "Fiscale" : (CAT_META[catOf(tpl)]?.label || "Varie")}</span>
                    <span className="truncate">{tpl.fornitore || tpl.descrizione || "(senza descrizione)"}{tpl.fornitore && tpl.descrizione ? ` · ${tpl.descrizione}` : ""}</span>
                    <span className="tabnum font-semibold text-amber-800 shrink-0">{fmtEur(tpl.totale)}</span>
                  </div>
                  <Button size="sm" variant="outline" className="h-7 px-2 text-xs shrink-0" onClick={() => addSingleRicorrente(tpl)} data-testid={`spese-ricorrente-add-${tpl.id}`}><Plus size={13} className="mr-1" /> Aggiungi</Button>
                </div>
              ))}
            </div>
          </div>
        )}
        <div className="space-y-5">
        {CATS.map((cat) => {
          const meta = CAT_META[cat];
          const rows = visible.filter((r) => catOf(r) === cat);
          const fornitoriForCat = cat === "utenze" ? fornitoriUtenze : fornitoriSpese;
          const sub = rows.reduce((s, r) => s + Number(r.totale || 0), 0);
          const perStruct = {};
          rows.forEach((r) => { perStruct[r.structure_id] = (perStruct[r.structure_id] || 0) + Number(r.totale || 0); });
          const Icon = meta.icon;
          return (
            <div className="gr-card overflow-hidden" data-testid={`spese-section-${cat}`} key={cat}>
              <div className="px-4 py-3 flex items-center justify-between border-b bg-secondary/40">
                <div className="font-head font-bold flex items-center gap-2" style={{ color: meta.color }}><Icon size={18} /> {meta.label} <span className="text-xs font-semibold text-muted-foreground">· {monthLabel(month)}</span></div>
                <Button size="sm" variant="outline" onClick={() => addRow(cat)} data-testid={`spese-add-${cat}`}><Plus size={14} className="mr-1" /> Aggiungi</Button>
              </div>
              <div className="overflow-x-auto">
                <Table className="min-w-[560px]">
                  <TableHeader>
                    <TableRow className="bg-secondary/30">
                      {showStruct && <TableHead className="font-bold text-xs px-2">Struttura</TableHead>}
                      <TableHead className="font-bold text-xs px-2">Fornitore</TableHead>
                      <TableHead className="font-bold text-xs px-2">Descrizione</TableHead>
                      <TableHead className="font-bold text-xs text-right px-1">Importo</TableHead>
                      <TableHead className="font-bold text-xs px-1">Stato</TableHead>
                      <TableHead className="w-7 px-1"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rows.length === 0 && <TableRow><TableCell colSpan={colSpanBase} className="text-center py-5 text-sm text-muted-foreground italic">Nessuna voce. Clicca "Aggiungi".</TableCell></TableRow>}
                    {rows.map((row) => (
                      <TableRow key={row.id} data-testid={`spese-row-${row.id}`}>
                        {showStruct && (
                          <TableCell className="py-1.5 px-2 align-top">
                            <Select value={row.structure_id || ""} onValueChange={(v) => patch(row, { structure_id: v })}>
                              <SelectTrigger className="h-8 w-[52px] min-w-0 text-[11px] px-1.5 font-bold justify-center" data-testid={`spese-struct-${row.id}`} title={structMap[row.structure_id]?.nome || "—"}><span>{structAbbr(structMap[row.structure_id]?.nome)}</span></SelectTrigger>
                              <SelectContent>{structOptions.map((s) => <SelectItem key={s.id} value={s.id}>{s.nome}</SelectItem>)}</SelectContent>
                            </Select>
                          </TableCell>
                        )}
                        <TableCell className="py-1.5 px-2 align-top">
                          <Select value={row.fornitore || ""} onValueChange={(v) => patch(row, { fornitore: v })}>
                            <SelectTrigger className="h-8 w-full min-w-[120px] text-[11px] px-1.5" data-testid={`spese-fornitore-${row.id}`}>
                              <SelectValue placeholder="Seleziona" />
                            </SelectTrigger>
                            <SelectContent>
                              {row.fornitore && !fornitoriForCat.some((x) => x.nome === row.fornitore) && <SelectItem value={row.fornitore}><span className="flex items-center gap-1.5">{fornitoreIcon(row.fornitore)}{row.fornitore}</span></SelectItem>}
                              {fornitoriForCat.map((x) => <SelectItem key={x.id} value={x.nome}><span className="flex items-center gap-1.5">{fornitoreIcon(x.nome)}{x.nome}</span></SelectItem>)}
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell className="py-1.5 px-2 align-top">
                          <input defaultValue={row.descrizione || ""} key={`d-${row.id}-${row.descrizione}`}
                            onBlur={(e) => { if (e.target.value !== (row.descrizione || "")) patch(row, { descrizione: e.target.value }); }}
                            className="w-full min-w-[300px] border rounded-md px-1.5 py-1.5 text-[11px] focus:border-primary outline-none" placeholder="Descrizione" data-testid={`spese-desc-${row.id}`} />
                        </TableCell>
                        <TableCell className="text-right py-1.5 px-1">
                          <div className="flex items-center justify-end gap-0.5">
                            <span className="text-[11px] text-muted-foreground">€</span>
                            <input type="number" defaultValue={row.totale ?? 0} key={`t-${row.id}-${row.totale}`}
                              onBlur={(e) => { const v = e.target.value === "" ? 0 : Number(e.target.value); if (v !== Number(row.totale || 0)) patch(row, { totale: v }); }}
                              className="w-20 border rounded-md px-1.5 py-1.5 text-[11px] text-right tabnum focus:border-primary outline-none" data-testid={`spese-importo-${row.id}`} />
                          </div>
                        </TableCell>
                        <TableCell className="py-1.5 px-1">
                          <button onClick={() => patch(row, { stato: (row.stato === "pagato" ? "non pagato" : "pagato") })} data-testid={`spese-stato-${row.id}`}
                            className={`text-[10px] font-semibold rounded-full px-1.5 py-1 border transition-colors whitespace-nowrap ${row.stato === "pagato" ? "bg-emerald-100 text-emerald-700 border-emerald-300" : "bg-rose-50 text-rose-600 border-rose-200"}`}>
                            {row.stato === "pagato" ? "Pagato" : "Non pag."}
                          </button>
                        </TableCell>
                        <TableCell className="py-1.5 px-1 whitespace-nowrap">
                          <button onClick={() => patch(row, { ricorrente: !row.ricorrente })} title={row.ricorrente ? "Ricorrente (proposta ogni mese)" : "Segna come ricorrente"} className="p-0.5 mr-0.5" data-testid={`spese-ricorrente-${row.id}`}>
                            <Star size={14} className={row.ricorrente ? "fill-amber-400 text-amber-500" : "text-muted-foreground"} />
                          </button>
                          <button onClick={() => removeRow(row.id)} className="text-muted-foreground hover:text-rose-600 p-0.5" data-testid={`spese-del-${row.id}`}><Trash2 size={14} /></button>
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow>
                      <TableCell colSpan={colSpanBase} className="py-1.5 px-2">
                        <button onClick={() => addRow(cat)} data-testid={`spese-add-row-${cat}`}
                          className="inline-flex items-center gap-1 text-[11px] font-semibold text-primary hover:underline">
                          <Plus size={13} /> Aggiungi riga
                        </button>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
              {showStruct && rows.length > 0 && (
                <div className="px-4 py-2 border-t bg-secondary/20 flex flex-wrap gap-x-4 gap-y-1 text-[11px]" data-testid={`spese-perstruct-${cat}`}>
                  <span className="font-bold uppercase tracking-wide text-muted-foreground">Per struttura:</span>
                  {structOptions.filter((s) => perStruct[s.id]).map((s) => (
                    <span key={s.id} className="font-semibold" data-testid={`spese-perstruct-${cat}-${s.id}`}>
                      <span className="text-muted-foreground">{s.nome}:</span> {fmtEur(perStruct[s.id])}
                    </span>
                  ))}
                </div>
              )}
              <div className="px-4 py-2.5 border-t bg-secondary/30 flex items-center justify-end gap-2" data-testid={`spese-total-foot-${cat}`}>
                <span className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground">Totale {meta.label}</span>
                <span className="font-head font-bold tabnum text-base" style={{ color: meta.color }} data-testid={`spese-subtotal-${cat}`}>{fmtEur(sub)}</span>
              </div>
            </div>
          );
        })}
        </div>

        <div className="space-y-5">
        <SpeseTabellaF24 month={month} showStruct={showStruct} structOptions={structOptions} fornitori={fornitoriSpese} f24Rows={f24Rows} f24Total={f24Total} f24ImpTot={f24ImpTot} f24RowsTotal={f24RowsTotal} patch={patch} addF24Row={addF24Row} removeRow={removeRow} />

        <SpeseTabellaFiscale month={month} showStruct={showStruct} structOptions={structOptions} fornitori={fornitoriSpese} fiscRows={fiscRows} fiscCapTot={fiscCapTot} fiscIndeducTot={fiscIndeducTot} fiscImportoTot={fiscImportoTot} patch={patch} addFiscaleRow={addFiscaleRow} removeRow={removeRow} />

        </div>

        <div className="gr-card p-6 bg-primary text-white flex items-center justify-between flex-wrap gap-2" data-testid="spese-grand-total-card">
          <div>
            <div className="font-head font-bold text-lg">{soloNonPagate ? "Totale da pagare" : "Totale spese del mese"}</div>
            <div className="text-xs opacity-80 mt-0.5">{monthLabel(month)} · {visible.length} voci{soloNonPagate ? " non pagate" : ""}{showStruct ? " · tutte le strutture" : ""}</div>
          </div>
          <div className="text-4xl font-head font-bold tabnum" data-testid="spese-grand-total">{fmtEur(shownTotal)}</div>
        </div>
      </div>
      )}

      <AlertDialog open={confirmCopy} onOpenChange={setConfirmCopy}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Copiare le spese dal mese precedente?</AlertDialogTitle>
            <AlertDialogDescription>
              Verranno copiate tutte le voci del mese precedente in <b>{monthLabel(month)}</b> (con stato "Non pagato"). Le voci già presenti non verranno rimosse. Potrai modificare gli importi dopo la copia.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={copiaMese} data-testid="spese-copy-confirm">Copia voci</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={showArch} onOpenChange={setShowArch}>
        <DialogContent className="max-w-2xl" data-testid="spese-archivio-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Archive size={18} /> Archivio spese</DialogTitle></DialogHeader>
          <div className="max-h-[60vh] overflow-auto">
            {archived.length === 0 ? (
              <div className="py-8 text-center text-sm text-muted-foreground" data-testid="spese-archivio-empty">Nessuna spesa in archivio.</div>
            ) : (
              <div className="divide-y">
                {archived.map((r) => (
                  <div key={r.id} className="flex items-center gap-3 py-2" data-testid={`spese-arch-row-${r.id}`}>
                    <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-secondary text-muted-foreground">{r.da_f24 ? "F24" : (CAT_META[catOf(r)]?.label || "Varie")}</span>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold truncate">{r.fornitore || r.descrizione || "(senza descrizione)"}</div>
                      <div className="text-xs text-muted-foreground">{monthLabel((r.data_documento || "").slice(0, 7))} · {fmtEur(r.totale)}</div>
                    </div>
                    <Button size="sm" variant="outline" onClick={() => restoreRow(r.id)} data-testid={`spese-restore-${r.id}`}><RotateCcw size={14} className="mr-1.5" /> Ripristina</Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
